import { FC, useState } from 'react'
import { Box, Typography, ClickAwayListener } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useThemeColors } from 'hooks/useThemeColors'
import useSearchLabel from 'hooks/useSearchLabel'
import { categoryToUrl } from 'ui/pages/library/utils'

import { Wrapper, Popper } from 'ui/pages/cpi-details/sections/devices/components/cpiNavigation.styles'
import CpiNavigationSearch from 'ui/pages/cpi-details/sections/devices/components/CpiNavigationSearch'
import { SkeletonBar } from '../sections/meta-data/metaDataSection.styles'

interface CategoryOption {
  id: string
  title: string
}

interface Props {
  categories: CategoryOption[]
  currentCategoryName: string
}

const CategoryNavigation: FC<Props> = ({ categories, currentCategoryName }) => {
  const getColor = useThemeColors()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [popperOpen, setPopperOpen] = useState(false)
  const navigate = useNavigate()

  const convertedOptions = categories.map((cat) => ({
    label: cat.title,
    name: cat.title,
    id: cat.id
  }))

  const { search, setSearch, labelOptions, highlightMatch } = useSearchLabel(convertedOptions, null, true)

  const isLoading = !currentCategoryName

  return (
    <ClickAwayListener
      onClickAway={() => {
        setPopperOpen(false)
        setAnchorEl(null)
        setSearch('')
      }}
    >
      <Wrapper className="category-navigation-wrapper">
        {isLoading ? (
          <SkeletonBar width="120px" sx={{ height: '18px' }} />
        ) : (
          <Box className="cpi-name-wrapper" onClick={() => setPopperOpen(!popperOpen)}>
            <Typography className="navigation-cpi-name" ref={(elm) => setAnchorEl(elm)}>
              {currentCategoryName}
            </Typography>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: popperOpen ? 'rotate(180deg)' : '',
                transition: 'all .2s ease-in-out'
              }}
            >
              <path
                d="M9.75 4.125L6 7.875L2.25 4.125"
                stroke={getColor('cta')}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        )}

        <Popper id="category-navigation-popper" open={popperOpen} anchorEl={anchorEl} placement="bottom-start">
          <Box className="content">
            <CpiNavigationSearch search={search} setSearch={setSearch} />

            <Box className="list-wrapper">
              <Box className="list">
                {labelOptions.map((option) => (
                  <Box
                    className="list-item"
                    key={option.label}
                    onClick={() => {
                      setPopperOpen(false)
                      setAnchorEl(null)
                      setSearch('')
                      // Navigate by the title-derived slug, not the backend id: the two diverge
                      // for Threat Detection (id detection-and-response) and IAM. Custom
                      // framework names are free text, so the slug needs encoding.
                      navigate(`/library/categories/${encodeURIComponent(categoryToUrl(option.label))}`)
                    }}
                  >
                    <Typography className="list-item-text">{highlightMatch(option.label)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Popper>
      </Wrapper>
    </ClickAwayListener>
  )
}

export default CategoryNavigation
